// A copilot proposal rendered as a reviewable per-field diff. The diff is
// computed against the CURRENT form state at render time, so it stays honest
// if the user keeps editing after the proposal arrived. Apply = shallow merge
// into the editor form (the page's patch()); nothing persists until Save.

import { useState } from "react";
import type { PatchProposal } from "../../services/assistant";
import { sameValue } from "../history/VersionHistory";

interface Props {
  proposal: PatchProposal;
  current: Record<string, unknown>;
  onApply: (patch: Record<string, unknown>) => void;
}

function fmt(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(empty)";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 1);
}

export function PatchCard({ proposal, current, onApply }: Props) {
  const [state, setState] = useState<"pending" | "applied" | "dismissed">("pending");

  if (state === "dismissed") return null;

  const fields = Object.entries(proposal.patch);
  return (
    <div className="patch-card">
      <div className="patch-rationale">{proposal.rationale}</div>
      {fields.map(([key, next]) => {
        const prev = current[key];
        const unchanged = sameValue(prev, next);
        return (
          <div className="patch-field" key={key}>
            <div className="patch-field-name">{key}{unchanged ? " · no change" : ""}</div>
            {!unchanged && <div className="patch-old">{fmt(prev)}</div>}
            <div className="patch-new">{fmt(next)}</div>
          </div>
        );
      })}
      <div className="patch-actions">
        {state === "applied" ? (
          <span className="patch-applied">Applied to the form — review and Save</span>
        ) : (
          <>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                onApply(proposal.patch);
                setState("applied");
              }}
            >
              Apply
            </button>
            <button className="btn btn-sm" onClick={() => setState("dismissed")}>
              Dismiss
            </button>
          </>
        )}
      </div>
    </div>
  );
}
